import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Phone, Mail, MapPin, Loader2, Send } from "lucide-react";
import SocialIcons from "@/components/SocialIcons";

const API = process.env.REACT_APP_BACKEND_URL;

const legal = [
  { label: "Privacy", href: "/privacy" },
  { label: "Terms & Conditions", href: "/terms" },
  { label: "Shipping & Delivery", href: "/shipping" },
  { label: "Refund & Cancellation", href: "/refund" },
  { label: "Disclaimer", href: "/disclaimer" },
  { label: "Cookies", href: "/cookies" },
];

const locations = [
  { label: "Mumbai", href: "/best-electronic-data-capture-software-for-clinical-trials-in-mumbai" },
  { label: "Hyderabad", href: "/best-electronic-data-capture-software-for-clinical-trials-in-hyderabad" },
  { label: "Ahmedabad", href: "/best-electronic-data-capture-software-for-clinical-trials-in-ahemdabad" },
  { label: "Pune", href: "/best-electronic-data-capture-software-for-clinical-trials-in-pune" },
  { label: "Delhi", href: "/best-electronic-data-capture-software-for-clinical-trials-in-delhi" },
];

const Column = ({ title, items, testid, span = "lg:col-span-2" }) => (
  <div className={span}>
    <div className="font-mono-tech text-[10px] tracking-[0.2em] text-slate-600 mb-4">{title}</div>
    <ul className="space-y-2.5 text-sm" data-testid={testid}>
      {items.map((item) => (
        <li key={item.label || item}>
          {item.href ? (
            <a href={item.href} className="hover:text-white transition-colors">{item.label}</a>
          ) : (
            <span className="flex items-center gap-2 text-slate-500">
              <MapPin className="w-3.5 h-3.5 text-emerald-500/70" /> {item}
            </span>
          )}
        </li>
      ))}
    </ul>
  </div>
);

export default function Footer() {
  const [subEmail, setSubEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    setSubscribing(true);
    try {
      await axios.post(`${API}/api/subscribe`, { email: subEmail, frequency: "weekly" });
      toast.success("You're subscribed", { description: "New articles will arrive in your inbox weekly." });
      setSubEmail("");
    } catch {
      toast.error("Could not subscribe", { description: "Please try again in a moment." });
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer data-keep-dark className="bg-[#04060B] border-t border-slate-800/80 text-slate-400">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-10 pb-6">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-8">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3.5">
              <span className="h-12 w-12 rounded-xl bg-white p-1.5 flex items-center justify-center shadow-[0_0_28px_-6px_rgba(16,185,129,0.5)]">
                <img src="/assets/etrialtrack-emblem.webp" alt="eTrialTrack logo" width="40" height="40" loading="lazy" decoding="async" className="h-full w-full object-contain" />
              </span>
              <div className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
                eTrial<span className="text-gradient-emerald">Track</span>
              </div>
            </div>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-500">
              The unified eClinical platform behind smarter, faster, fully compliant
              clinical trials — trusted across 24 countries.
            </p>
            <div className="mt-5 space-y-2.5 text-sm">
              <a href="tel:+919873514525" data-testid="footer-phone-link"
                className="flex items-center gap-3 hover:text-emerald-400 transition-colors">
                <Phone className="w-4 h-4 text-emerald-500" /> +91 9873514525
              </a>
              <a href="mailto:info@etrialtrack.in" data-testid="footer-email-link"
                className="flex items-center gap-3 hover:text-emerald-400 transition-colors">
                <Mail className="w-4 h-4 text-emerald-500" /> info@etrialtrack.in
              </a>
              <div data-testid="footer-address" className="flex items-start gap-3 text-slate-500">
                <MapPin className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                <span>
                  Plot No 19, Off No 102, Veer Savarkar Block,<br />
                  East Delhi, New Delhi, New Delhi — 110092
                </span>
              </div>
              <a
                href="https://maps.app.goo.gl/xVcHRYSexqBAiA2q7"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-map-link"
                className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-400 hover:text-emerald-300 transition-colors"
              >
                <MapPin className="w-4 h-4" /> Locate us on Google Maps
                <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </div>
            <a
              href="/#demo"
              data-testid="footer-schedule-demo-btn"
              className="group mt-6 inline-flex items-center gap-2 border border-emerald-500/40 hover:border-emerald-400 text-emerald-400 hover:text-emerald-300 rounded-full px-6 py-3 text-sm font-semibold transition-colors duration-300"
            >
              Schedule Demo
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <SocialIcons />
          </div>
          <Column title="LEGAL" items={legal} testid="footer-legal-menu" />
          <Column title="LOCATIONS" items={locations} testid="footer-locations-menu" />
          <div className="lg:col-span-3 flex flex-col">
            <div className="font-mono-tech text-[10px] tracking-[0.2em] text-slate-600 mb-4">NEWSLETTER</div>
            <p className="text-sm text-slate-500 mb-4 leading-relaxed">
              New blogs and articles in your inbox — weekly. Unsubscribe anytime.
            </p>
            <form onSubmit={onSubscribe} data-testid="footer-subscribe-form" className="space-y-2.5">
              <input
                type="email"
                required
                value={subEmail}
                onChange={(e) => setSubEmail(e.target.value)}
                placeholder="you@company.com"
                data-testid="footer-subscribe-email-input"
                className="w-full bg-slate-800/70 border border-white/10 focus:border-emerald-500/60 rounded-full px-5 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none transition-colors"
              />
              <button
                type="submit"
                disabled={subscribing}
                data-testid="footer-subscribe-submit"
                className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-70 text-slate-950 text-sm font-semibold rounded-full px-5 py-2.5 btn-glow transition-colors"
              >
                {subscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Subscribe
              </button>
            </form>
            <div className="mt-5 flex-1 flex flex-col min-h-0" data-testid="footer-near-me">
              <div className="font-mono-tech text-[10px] tracking-[0.2em] text-slate-600 mb-2.5">NEAR ME</div>
              <div className="relative flex-1 min-h-[140px] rounded-xl overflow-hidden border border-white/10">
                <iframe
                  title="eTrialTrack office — locate us on Google Maps"
                  src="https://www.google.com/maps/embed?pb=!4v1790106916407!6m8!1m7!1sCAoSHENJQUJJaERUMDYxNjlUc3J4VkJFZXNaSkVSUzE.!2m2!1d28.63532025070052!2d77.28521813486236!3f139.9106!4f0!5f0.7820865974627469"
                  className="absolute inset-0 w-full h-full"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                  data-testid="footer-near-me-map"
                />
              </div>
              <p className="mt-2.5 text-xs text-slate-500 leading-relaxed">
                Searching for clinical trial software near you? Visit our East Delhi, New Delhi office — find us on the map above.
              </p>
            </div>
          </div>
        </div>
        <div className="mt-10 pt-5 border-t border-slate-800/70 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-mono-tech text-[10px] tracking-[0.18em] text-slate-600">
            © 2026 ETRIALTRACK. ALL RIGHTS RESERVED.
          </p>
          <p className="font-mono-tech text-[10px] tracking-[0.18em] text-slate-600">
            FDA 21 CFR PART 11 • ICH-GCP • HIPAA • GDPR • ISO 27001
          </p>
        </div>
      </div>
    </footer>
  );
}
